import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ArrowRight, Star, ShieldCheck, Clock, BadgeCheck, Headphones, Layers, Award, PencilRuler, Hammer, KeyRound, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/hooks/useSettings";
import { RevealHeading, Reveal, Label } from "@/components/site/Motion";
import { CTASection } from "@/components/site/Sections";
import HomeEnquiry from "@/components/site/HomeEnquiry";
import WorkInProgress from "@/components/site/WorkInProgress";
import CostCalculator from "@/components/site/CostCalculator";
import { serviceIcon } from "@/data/icons";
import { MANIFESTO, SERVICES, DIVISIONS } from "@/data/content";

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1780597257900-303ae731f1b8?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1709496709586-ad1467b5d738?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1761479135364-4240520b5793?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&q=85",
];

function Hero() {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const settings = useSettings();
  const images = Array.isArray(settings.hero_images) && settings.hero_images.length
    ? settings.hero_images
    : HERO_IMAGES;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    setIdx(0);
  }, [images.length]);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 4500);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden bg-navy-deep">
      <motion.div style={{ scale }} className="absolute inset-0 z-0">
        <AnimatePresence mode="sync">
          <motion.img
            key={idx}
            src={images[idx]}
            alt="Luxury home built by United Youngs India"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeInOut" }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/50 to-navy-deep/30" />
      </motion.div>

      <motion.div style={{ y }} className="relative z-10 w-full mx-auto max-w-7xl px-5 lg:px-8 pb-20 lg:pb-28 pt-40">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <Label>United Youngs India · Est. Lucknow</Label>
        </motion.div>

        <h1 className="mt-6 font-display text-white text-5xl md:text-7xl lg:text-[7rem] font-medium tracking-tighter leading-[0.92]">
          <RevealHeading lines={["We build", "dreams into", "landmarks."]} />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-md text-white/70 leading-relaxed">
            Construction, interior design & bespoke furniture — crafted with 25+
            years of precision across Lucknow & Uttar Pradesh.
          </p>
          <div className="flex items-center gap-4">
            <Link
              to="/contact"
              data-testid="hero-cta"
              className="group inline-flex items-center gap-3 rounded-sm bg-gold px-8 py-4 text-navy font-medium hover:bg-white transition-colors duration-300"
            >
              Start your project
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 text-white border-b border-white/30 pb-1 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              View work
            </Link>
          </div>
        </motion.div>

        <div className="mt-10 flex gap-2" data-testid="hero-dots">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-1 rounded-full transition-all duration-500 ${i === idx ? "w-10 bg-gold" : "w-5 bg-white/30"}`}
            />
          ))}
        </div>
      </motion.div>
    </section>
  );
}

function Ribbon() {
  const items = ["Residential Construction", "Interior Design", "Custom Furniture", "2D & 3D Design", "Renovation", "Commercial Projects"];
  return (
    <div className="bg-cream border-y border-navy/10 py-6">
      <Marquee speed={40} gradient={false}>
        {items.concat(items).map((t, i) => (
          <span key={i} className="mx-10 inline-flex items-center gap-10 font-display text-2xl md:text-3xl text-navy/80">
            {t} <span className="text-gold">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function Intro() {
  const s = useSettings();
  return (
    <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32">
      <div className="grid gap-12 md:grid-cols-12 items-start">
        <div className="md:col-span-7">
          <Label>Who we are</Label>
          <Reveal>
            <h2 className="mt-5 font-display text-3xl md:text-4xl lg:text-5xl font-light leading-tight tracking-tight text-navy text-balance">
              A premier construction & interior design house, dedicated to
              transforming spaces and turning visions into reality.
            </h2>
          </Reveal>
        </div>
        <div className="md:col-span-5 md:pt-4">
          <Reveal delay={0.15}>
            <p className="text-navy/60 leading-relaxed">{s.about}</p>
            <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-navy font-medium hover:text-gold transition-colors">
              More about UYI <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="relative grain overflow-hidden bg-navy text-white">
      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32">
        <Label>The UYI philosophy</Label>
        <div className="mt-12 grid gap-x-12 gap-y-14 md:grid-cols-2">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.no} delay={i * 0.08}>
              <div className="group border-t border-white/15 pt-6">
                <div className="flex items-baseline gap-5">
                  <span className="font-display text-gold text-2xl">{m.no}</span>
                  <h3 className="font-display text-2xl md:text-3xl font-normal">{m.title}</h3>
                </div>
                <p className="mt-4 md:pl-12 text-white/55 leading-relaxed">{m.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesPreview() {
  const { data: services = [] } = useQuery({
    queryKey: ["services"],
    queryFn: async () => (await api.get("/services")).data,
  });
  const list = services.length ? services : SERVICES.map((s, i) => ({ ...s, icon: "building", id: i }));
  return (
    <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <div>
          <Label>What we do</Label>
          <h2 className="mt-5 font-display text-4xl md:text-5xl font-light tracking-tight text-navy">Our services</h2>
        </div>
        <Link to="/services" className="inline-flex items-center gap-2 text-navy hover:text-gold transition-colors">
          All services <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="grid gap-px bg-navy/10 border border-navy/10 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((sv, i) => {
          const Icon = serviceIcon(sv.icon);
          return (
            <Reveal key={sv.id || i} delay={(i % 3) * 0.06}>
              <div className="group bg-cream p-8 lg:p-10 h-full hover:bg-navy transition-colors duration-500">
                <Icon className="h-9 w-9 text-gold" strokeWidth={1.3} />
                <h3 className="mt-6 font-display text-xl text-navy group-hover:text-white transition-colors duration-500">{sv.title}</h3>
                <p className="mt-3 text-sm text-navy/55 group-hover:text-white/60 leading-relaxed transition-colors duration-500">{sv.description || sv.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function FeaturedWork() {
  const { data: projects = [] } = useQuery({
    queryKey: ["projects", "featured"],
    queryFn: async () => (await api.get("/projects")).data,
  });
  const featured = projects.filter((p) => p.featured).slice(0, 5);
  const list = featured.length ? featured : projects.slice(0, 5);

  return (
    <section className="bg-cream border-t border-navy/10 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <Label>Selected projects</Label>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-light tracking-tight text-navy">Featured work</h2>
          </div>
          <Link to="/portfolio" className="inline-flex items-center gap-2 text-navy hover:text-gold transition-colors">
            Full portfolio <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid gap-5 md:grid-cols-12 md:auto-rows-[280px]">
          {list.map((p, i) => {
            const span =
              i === 0 ? "md:col-span-8 md:row-span-2" : i === 1 ? "md:col-span-4" : i === 2 ? "md:col-span-4" : "md:col-span-6";
            return (
              <Reveal key={p.id} delay={(i % 3) * 0.06} className={span}>
                <Link to="/portfolio" className="group relative block h-full min-h-[280px] overflow-hidden rounded-sm bg-navy">
                  <img src={p.image} alt={p.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/90 via-navy-deep/10 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <span className="text-xs uppercase tracking-[0.2em] text-gold">{p.category}{p.location ? ` · ${p.location}` : ""}</span>
                    <h3 className="mt-2 font-display text-xl md:text-2xl text-white">{p.title}</h3>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const WHY = [
  { icon: Layers, title: "One-Stop Solution", desc: "Architecture, construction, interiors & furniture — everything under one trusted roof." },
  { icon: Award, title: "500+ Projects", desc: "Residential & commercial projects delivered across Lucknow & Uttar Pradesh." },
  { icon: ShieldCheck, title: "Structure Warranty", desc: "Engineering-grade construction backed by a written structural warranty." },
  { icon: BadgeCheck, title: "Quality Control", desc: "Rigorous material checks and on-site supervision at every stage." },
  { icon: Headphones, title: "24-Hour Support", desc: "A responsive team that resolves your queries within a day." },
  { icon: Clock, title: "On-Time Delivery", desc: "Transparent timelines and milestones, honoured to the day." },
];

function WhyChooseUs() {
  return (
    <section className="relative grain overflow-hidden bg-navy text-white py-24 lg:py-32" data-testid="why-choose-us">
      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-8">
        <Label>Why build with UYI</Label>
        <h2 className="mt-5 font-display text-4xl md:text-5xl font-light tracking-tight mb-14 max-w-2xl">
          A single, accountable partner from blueprint to handover.
        </h2>
        <div className="grid gap-px bg-white/10 border border-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w, i) => {
            const Icon = w.icon;
            return (
              <Reveal key={w.title} delay={(i % 3) * 0.06}>
                <div className="group bg-navy p-8 lg:p-10 h-full hover:bg-navy-light transition-colors duration-400">
                  <Icon className="h-9 w-9 text-gold" strokeWidth={1.3} />
                  <h3 className="mt-6 font-display text-xl">{w.title}</h3>
                  <p className="mt-3 text-sm text-white/55 leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { icon: MessageSquare, title: "Consultation", desc: "We understand your plot, needs, timeline and budget." },
  { icon: PencilRuler, title: "Design & 3D", desc: "Floor plans and photoreal 2D/3D visualisation for your approval." },
  { icon: Layers, title: "Transparent Estimate", desc: "A clear, itemised quote — with material options and no hidden costs." },
  { icon: Hammer, title: "Construction", desc: "Quality-controlled build with regular on-site progress updates." },
  { icon: KeyRound, title: "Handover", desc: "Finishing, inspection and a warranty-backed handover of your space." },
];

function HowWeBuild() {
  return (
    <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32" data-testid="how-we-build">
      <Label>Our process</Label>
      <h2 className="mt-5 font-display text-4xl md:text-5xl font-light tracking-tight text-navy mb-14">How we build your dream</h2>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <Reveal key={s.title} delay={i * 0.07}>
              <div className="relative h-full rounded-sm border border-navy/10 bg-white p-6">
                <span className="font-display text-5xl text-navy/10">{String(i + 1).padStart(2, "0")}</span>
                <Icon className="h-8 w-8 text-gold mt-2" strokeWidth={1.3} />
                <h3 className="mt-4 font-display text-lg text-navy">{s.title}</h3>
                <p className="mt-2 text-sm text-navy/55 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function DivisionsPreview() {
  const list = Object.values(DIVISIONS);
  return (
    <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32">
      <Label>Three houses, one standard</Label>
      <h2 className="mt-5 font-display text-4xl md:text-5xl font-light tracking-tight text-navy mb-14">Our divisions</h2>
      <div className="grid gap-6 md:grid-cols-3">
        {list.map((d, i) => {
          const Icon = d.icon;
          return (
            <Reveal key={d.key} delay={i * 0.08}>
              <Link to={`/divisions/${d.key}`} className="group block overflow-hidden rounded-sm border border-navy/10 bg-white">
                <div className="relative h-60 overflow-hidden">
                  <img src={d.hero} alt={d.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-navy/20 group-hover:bg-navy/0 transition-colors duration-500" />
                </div>
                <div className="p-7">
                  <Icon className="h-8 w-8 text-gold" strokeWidth={1.3} />
                  <h3 className="mt-4 font-display text-2xl text-navy">{d.name}</h3>
                  <p className="mt-2 text-sm text-navy/55 leading-relaxed">{d.tagline}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm text-navy group-hover:text-gold transition-colors">
                    Explore <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

const REVIEWS = [
  { name: "Homeowner, Lucknow", text: "UYI delivered our villa exactly as visualised — quality, precision and commitment in every corner.", rating: 5 },
  { name: "Commercial Client", text: "Professional team from design to handover. The 3D plans matched the finished build perfectly.", rating: 5 },
  { name: "Interior Client", text: "Our home feels like a luxury showroom now. Craftsmanship on the furniture is outstanding.", rating: 5 },
];

function Reviews() {
  const { data: testimonials = [] } = useQuery({
    queryKey: ["testimonials"],
    queryFn: async () => (await api.get("/testimonials")).data,
  });
  const list = testimonials.length ? testimonials : REVIEWS;
  return (
    <section className="bg-cream border-t border-navy/10 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Label>Client stories</Label>
        <h2 className="mt-5 font-display text-4xl md:text-5xl font-light tracking-tight text-navy mb-14">Trusted across Lucknow</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {list.map((r, i) => (
            <Reveal key={r.id || i} delay={i * 0.08}>
              <div className="h-full rounded-sm border border-navy/10 bg-white p-8">
                <div className="flex gap-1 text-gold">
                  {Array.from({ length: r.rating || 5 }).map((_, k) => (
                    <Star key={k} className="h-4 w-4 fill-gold" />
                  ))}
                </div>
                <p className="mt-5 font-display text-lg text-navy leading-relaxed">"{r.text}"</p>
                <p className="mt-6 text-sm uppercase tracking-[0.2em] text-navy/50">{r.name}{r.role ? ` · ${r.role}` : ""}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Ribbon />
      <Intro />
      <WhyChooseUs />
      <Manifesto />
      <ServicesPreview />
      <HowWeBuild />
      <FeaturedWork />
      <WorkInProgress />
      <DivisionsPreview />
      <Reviews />
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32">
        <CostCalculator />
      </section>
      <HomeEnquiry />
      <CTASection />
    </div>
  );
}
